/**
 * Printable QR sticker rendering for Bilt build profiles.
 *
 * Vinyl-ready templates (2in kit, square sticker, windshield) are single-ink
 * black-on-white so owners can cut/print one-color decals. Event card and
 * show board keep a darker presentation for display boards.
 *
 * The QR image is always drawn dark-on-white inside a quiet-zone panel so
 * scans stay reliable. Server-generated QRs already embed the Bilt mark.
 */
export type QrStickerTemplate = 'square_2in' | 'square_sticker' | 'windshield' | 'event_card' | 'show_board';

/** Public path for the monochrome Bilt B-mark used on vinyl stickers. */
export const BILT_MARK_MONO_SRC = '/brand/bilt-mark-mono.png';

export interface QrStickerTemplateSpec {
  width: number;
  height: number;
  label: string;
  /** Short use-case caption for pickers. */
  useCase: string;
  /** Physical print size at 300 DPI. */
  physicalSize: string;
  /** Whether the template can include a vehicle photo. */
  supportsVehicleImage: boolean;
}

export const QR_STICKER_TEMPLATES: Record<QrStickerTemplate, QrStickerTemplateSpec> = {
  square_2in: {
    width: 900,
    height: 900,
    label: '2-inch QR kit',
    useCase: '2in square print kit',
    physicalSize: '2 x 2 in',
    supportsVehicleImage: false,
  },
  square_sticker: {
    width: 1200,
    height: 1200,
    label: 'Square sticker',
    useCase: '4in square sticker',
    physicalSize: '4 x 4 in',
    supportsVehicleImage: false,
  },
  windshield: {
    width: 1800,
    height: 600,
    label: 'Windshield strip',
    useCase: 'Windshield strip',
    physicalSize: '6 x 2 in',
    supportsVehicleImage: false,
  },
  event_card: {
    width: 1500,
    height: 2100,
    label: 'Event card',
    useCase: '5x7 event card',
    physicalSize: '5 x 7 in',
    supportsVehicleImage: true,
  },
  show_board: {
    width: 3300,
    height: 2550,
    label: 'Show board',
    useCase: 'Show board 11x8.5',
    physicalSize: '11 x 8.5 in',
    supportsVehicleImage: true,
  },
};

export interface QrStickerOptions {
  template: QrStickerTemplate;
  vehicleName: string;
  /** Short display URL, e.g. "biltspec.com/user/slug". */
  shortUrl: string;
  /** Already-loaded QR image element (from the app's existing QR source). */
  qrImage: HTMLImageElement;
  /** Monochrome Bilt B-mark (black on transparent), for vinyl lockups. */
  brandMark?: HTMLImageElement | null;
  /** Approved accent hex color; omit for the neutral treatment. */
  accentColor?: string | null;
  /** Only used on event_card and show_board templates. */
  vehicleImage?: HTMLImageElement | null;
}

const FONT_STACK = "-apple-system, 'Helvetica Neue', Arial, sans-serif";

/** Dark display templates (event card / show board). */
const COLORS = {
  bg: '#09090b',
  text: '#fafafa',
  muted: '#a1a1aa',
  dim: '#71717a',
  border: 'rgba(255,255,255,0.14)',
};

/** Single-ink vinyl / decal templates. */
const VINYL = {
  bg: '#ffffff',
  ink: '#000000',
  muted: '#222222',
  hairline: '#000000',
};

function font(weight: number | string, size: number): string {
  return `${weight} ${Math.round(size)}px ${FONT_STACK}`;
}

function truncate(ctx: CanvasRenderingContext2D, text: string, maxWidth: number): string {
  if (ctx.measureText(text).width <= maxWidth) return text;
  let out = text;
  while (out.length > 1 && ctx.measureText(`${out}…`).width > maxWidth) {
    out = out.slice(0, -1);
  }
  return `${out}…`;
}

function wrapText(
  ctx: CanvasRenderingContext2D,
  text: string,
  maxWidth: number,
  maxLines: number
): string[] {
  const words = text.split(/\s+/).filter(Boolean);
  const lines: string[] = [];
  let current = '';
  for (const word of words) {
    const test = current ? `${current} ${word}` : word;
    if (ctx.measureText(test).width > maxWidth && current) {
      lines.push(current);
      current = word;
      if (lines.length >= maxLines) {
        current = '';
        break;
      }
    } else {
      current = test;
    }
  }
  if (current && lines.length < maxLines) lines.push(current);
  const last = lines.length - 1;
  if (last >= 0) lines[last] = truncate(ctx, lines[last], maxWidth);
  return lines;
}

function roundRectPath(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  r: number
) {
  const radius = Math.min(r, w / 2, h / 2);
  ctx.beginPath();
  ctx.moveTo(x + radius, y);
  ctx.arcTo(x + w, y, x + w, y + h, radius);
  ctx.arcTo(x + w, y + h, x, y + h, radius);
  ctx.arcTo(x, y + h, x, y, radius);
  ctx.arcTo(x, y, x + w, y, radius);
  ctx.closePath();
}

function drawImageCover(
  ctx: CanvasRenderingContext2D,
  img: HTMLImageElement,
  x: number,
  y: number,
  w: number,
  h: number
) {
  const iw = img.naturalWidth || img.width;
  const ih = img.naturalHeight || img.height;
  if (!iw || !ih) return;
  const scale = Math.max(w / iw, h / ih);
  const sw = w / scale;
  const sh = h / scale;
  const sx = (iw - sw) / 2;
  const sy = (ih - sh) * 0.4;
  ctx.drawImage(img, sx, sy, sw, sh, x, y, w, h);
}

/**
 * Draws the QR on a white rounded panel with an enforced quiet zone.
 * Nothing else may be drawn inside the returned panel rect.
 * Returns the panel size so layouts can position around it.
 */
function drawQrPanel(
  ctx: CanvasRenderingContext2D,
  qr: HTMLImageElement,
  x: number,
  y: number,
  qrSize: number
): number {
  // Quiet zone: at least 4 modules, approximated as 8% of the QR size.
  // Use 10% for extra safety margin on printed material.
  const quiet = Math.max(24, Math.round(qrSize * 0.1));
  const panel = qrSize + quiet * 2;
  ctx.fillStyle = '#ffffff';
  roundRectPath(ctx, x, y, panel, panel, Math.round(quiet * 0.75));
  ctx.fill();
  ctx.drawImage(qr, x + quiet, y + quiet, qrSize, qrSize);
  return panel;
}

function qrPanelSize(qrSize: number): number {
  const quiet = Math.max(24, Math.round(qrSize * 0.1));
  return qrSize + quiet * 2;
}

function drawAccentBar(
  ctx: CanvasRenderingContext2D,
  accent: string | null | undefined,
  x: number,
  y: number,
  w: number,
  h: number
) {
  ctx.fillStyle = accent || COLORS.border;
  ctx.fillRect(x, y, w, h);
}

function drawBrand(ctx: CanvasRenderingContext2D, x: number, y: number, size: number, ink = COLORS.text) {
  ctx.fillStyle = ink;
  ctx.font = font(700, size);
  ctx.fillText('BILT', x, y);
}

/**
 * Draw the Bilt B-mark (black-on-transparent PNG). Falls back to wordmark-only
 * spacing if the mark image is unavailable.
 */
function drawBiltMark(
  ctx: CanvasRenderingContext2D,
  mark: HTMLImageElement | null | undefined,
  x: number,
  y: number,
  size: number,
  ink = VINYL.ink
) {
  if (!mark || mark.naturalWidth <= 0) return;
  if (ink === VINYL.ink || ink === '#000000') {
    ctx.drawImage(mark, x, y, size, size);
    return;
  }
  // Recolor for light-on-dark templates without affecting the rest of the canvas.
  const off = document.createElement('canvas');
  off.width = size;
  off.height = size;
  const octx = off.getContext('2d');
  if (!octx) {
    ctx.drawImage(mark, x, y, size, size);
    return;
  }
  octx.fillStyle = ink;
  octx.fillRect(0, 0, size, size);
  octx.globalCompositeOperation = 'destination-in';
  octx.drawImage(mark, 0, 0, size, size);
  ctx.drawImage(off, x, y);
}

function drawBrandLockup(
  ctx: CanvasRenderingContext2D,
  mark: HTMLImageElement | null | undefined,
  cx: number,
  y: number,
  markSize: number,
  textSize: number,
  ink = VINYL.ink
) {
  ctx.save();
  ctx.textAlign = 'left';
  ctx.textBaseline = 'middle';
  ctx.font = font(700, textSize);
  const label = 'BILT';
  const gap = Math.round(markSize * 0.22);
  const textW = ctx.measureText(label).width;
  const total = markSize + gap + textW;
  const left = cx - total / 2;
  drawBiltMark(ctx, mark, left, y - markSize / 2, markSize, ink);
  ctx.fillStyle = ink;
  ctx.fillText(label, left + markSize + gap, y);
  ctx.restore();
}

function drawSquare2In(ctx: CanvasRenderingContext2D, options: QrStickerOptions) {
  const { width, height } = QR_STICKER_TEMPLATES.square_2in;
  ctx.fillStyle = VINYL.bg;
  ctx.fillRect(0, 0, width, height);

  // Hairline frame — cuts cleanly as one-color vinyl.
  ctx.strokeStyle = VINYL.hairline;
  ctx.lineWidth = 4;
  ctx.strokeRect(18, 18, width - 36, height - 36);

  ctx.textAlign = 'center';
  ctx.textBaseline = 'alphabetic';

  drawBrandLockup(ctx, options.brandMark, width / 2, 72, 36, 34, VINYL.ink);

  ctx.fillStyle = VINYL.ink;
  ctx.font = font(700, 40);
  const nameLines = wrapText(ctx, options.vehicleName, width - 100, 2);
  let y = nameLines.length > 1 ? 128 : 140;
  for (const line of nameLines) {
    ctx.fillText(line, width / 2, y);
    y += 46;
  }

  const qrSize = 460;
  const panel = qrPanelSize(qrSize);
  // On white vinyl art, skip the extra white panel fill — keep quiet zone via padding.
  const quiet = Math.max(24, Math.round(qrSize * 0.1));
  const panelX = (width - panel) / 2;
  const panelY = y + 8;
  ctx.drawImage(options.qrImage, panelX + quiet, panelY + quiet, qrSize, qrSize);

  const belowQr = panelY + panel;
  ctx.fillStyle = VINYL.muted;
  ctx.font = font(500, 22);
  ctx.fillText('Scan for the full build', width / 2, belowQr + 40);
  ctx.fillStyle = VINYL.ink;
  ctx.font = font(600, 26);
  ctx.fillText(truncate(ctx, options.shortUrl, width - 100), width / 2, belowQr + 78);

  drawBrandLockup(ctx, options.brandMark, width / 2, height - 48, 28, 26, VINYL.ink);
}

function drawSquareSticker(ctx: CanvasRenderingContext2D, options: QrStickerOptions) {
  const { width, height } = QR_STICKER_TEMPLATES.square_sticker;
  ctx.fillStyle = VINYL.bg;
  ctx.fillRect(0, 0, width, height);

  ctx.strokeStyle = VINYL.hairline;
  ctx.lineWidth = 6;
  ctx.strokeRect(28, 28, width - 56, height - 56);

  ctx.textAlign = 'center';
  ctx.textBaseline = 'alphabetic';

  drawBrandLockup(ctx, options.brandMark, width / 2, 100, 48, 44, VINYL.ink);

  ctx.fillStyle = VINYL.ink;
  ctx.font = font(700, 52);
  const nameLines = wrapText(ctx, options.vehicleName, width - 160, 2);
  let y = nameLines.length > 1 ? 176 : 196;
  for (const line of nameLines) {
    ctx.fillText(line, width / 2, y);
    y += 60;
  }

  const qrSize = 600;
  const panel = qrPanelSize(qrSize);
  const quiet = Math.max(24, Math.round(qrSize * 0.1));
  const panelX = (width - panel) / 2;
  const panelY = 280;
  ctx.drawImage(options.qrImage, panelX + quiet, panelY + quiet, qrSize, qrSize);

  const belowQr = panelY + panel;
  ctx.fillStyle = VINYL.muted;
  ctx.font = font(500, 32);
  ctx.fillText('Scan for the full build', width / 2, belowQr + 64);
  ctx.fillStyle = VINYL.ink;
  ctx.font = font(600, 36);
  ctx.fillText(truncate(ctx, options.shortUrl, width - 160), width / 2, belowQr + 116);

  drawBrandLockup(ctx, options.brandMark, width / 2, height - 72, 40, 36, VINYL.ink);
}

function drawWindshield(ctx: CanvasRenderingContext2D, options: QrStickerOptions) {
  const { width, height } = QR_STICKER_TEMPLATES.windshield;
  ctx.fillStyle = VINYL.bg;
  ctx.fillRect(0, 0, width, height);

  ctx.strokeStyle = VINYL.hairline;
  ctx.lineWidth = 5;
  ctx.strokeRect(20, 20, width - 40, height - 40);

  const qrSize = 400;
  const panel = qrPanelSize(qrSize);
  const quiet = Math.max(24, Math.round(qrSize * 0.1));
  const panelX = width - panel - 60;
  const panelY = (height - panel) / 2;
  ctx.drawImage(options.qrImage, panelX + quiet, panelY + quiet, qrSize, qrSize);

  const leftPad = 96;
  const textMax = panelX - leftPad - 60;

  ctx.textAlign = 'left';
  ctx.textBaseline = 'alphabetic';
  drawBiltMark(ctx, options.brandMark, leftPad, 56, 52, VINYL.ink);
  ctx.fillStyle = VINYL.ink;
  ctx.font = font(700, 44);
  ctx.fillText('BILT', leftPad + 64, 96);

  ctx.font = font(700, 78);
  const nameLines = wrapText(ctx, options.vehicleName, textMax, 2);
  let y = 220;
  for (const line of nameLines) {
    ctx.fillText(line, leftPad, y);
    y += 90;
  }

  ctx.fillStyle = VINYL.muted;
  ctx.font = font(500, 34);
  ctx.fillText('Scan for the full build profile', leftPad, height - 122);
  ctx.fillStyle = VINYL.ink;
  ctx.font = font(600, 36);
  ctx.fillText(truncate(ctx, options.shortUrl, textMax), leftPad, height - 66);
}

function drawEventCard(ctx: CanvasRenderingContext2D, options: QrStickerOptions) {
  const { width, height } = QR_STICKER_TEMPLATES.event_card;
  ctx.fillStyle = COLORS.bg;
  ctx.fillRect(0, 0, width, height);

  let contentTop = 140;
  if (options.vehicleImage) {
    const imageHeight = 720;
    ctx.save();
    ctx.beginPath();
    ctx.rect(0, 0, width, imageHeight);
    ctx.clip();
    drawImageCover(ctx, options.vehicleImage, 0, 0, width, imageHeight);
    ctx.restore();
    const scrim = ctx.createLinearGradient(0, imageHeight * 0.4, 0, imageHeight);
    scrim.addColorStop(0, 'rgba(9,9,11,0)');
    scrim.addColorStop(1, 'rgba(9,9,11,1)');
    ctx.fillStyle = scrim;
    ctx.fillRect(0, 0, width, imageHeight);
    contentTop = imageHeight + 20;
  }
  drawAccentBar(ctx, options.accentColor, 0, 0, width, 18);

  ctx.textAlign = 'center';
  ctx.textBaseline = 'alphabetic';

  ctx.fillStyle = COLORS.text;
  ctx.font = font(700, 84);
  const nameLines = wrapText(ctx, options.vehicleName, width - 240, 2);
  let y = contentTop + (options.vehicleImage ? 60 : 140);
  for (const line of nameLines) {
    ctx.fillText(line, width / 2, y);
    y += 98;
  }

  const qrSize = 660;
  const panel = qrPanelSize(qrSize);
  const qrY = y + 50;
  drawQrPanel(ctx, options.qrImage, (width - panel) / 2, qrY, qrSize);

  const belowQr = qrY + panel;
  ctx.fillStyle = COLORS.muted;
  ctx.font = font(500, 44);
  ctx.fillText('Scan for the full build', width / 2, belowQr + 96);
  ctx.fillStyle = COLORS.text;
  ctx.font = font(600, 48);
  ctx.fillText(truncate(ctx, options.shortUrl, width - 240), width / 2, belowQr + 168);

  ctx.strokeStyle = COLORS.border;
  ctx.lineWidth = 2;
  ctx.beginPath();
  ctx.moveTo(140, height - 170);
  ctx.lineTo(width - 140, height - 170);
  ctx.stroke();
  ctx.fillStyle = COLORS.dim;
  ctx.font = font(700, 48);
  ctx.fillText('BILT', width / 2, height - 84);
}

function drawShowBoard(ctx: CanvasRenderingContext2D, options: QrStickerOptions) {
  const { width, height } = QR_STICKER_TEMPLATES.show_board;
  ctx.fillStyle = COLORS.bg;
  ctx.fillRect(0, 0, width, height);

  const qrSize = 1150;
  const panel = qrPanelSize(qrSize);
  const panelX = width - panel - 200;
  const panelY = (height - panel) / 2;
  const leftPad = 200;
  const textMax = panelX - leftPad - 120;

  if (options.vehicleImage) {
    const imageHeight = Math.round(height * 0.46);
    ctx.save();
    ctx.beginPath();
    ctx.rect(0, 0, width, imageHeight);
    ctx.clip();
    drawImageCover(ctx, options.vehicleImage, 0, 0, width, imageHeight);
    ctx.restore();
    const scrim = ctx.createLinearGradient(0, imageHeight * 0.3, 0, imageHeight);
    scrim.addColorStop(0, 'rgba(9,9,11,0.1)');
    scrim.addColorStop(1, 'rgba(9,9,11,1)');
    ctx.fillStyle = scrim;
    ctx.fillRect(0, 0, width, imageHeight);
    // Keep the QR panel readable if it overlaps the photo region.
    const sideScrim = ctx.createLinearGradient(panelX - 300, 0, panelX, 0);
    sideScrim.addColorStop(0, 'rgba(9,9,11,0)');
    sideScrim.addColorStop(1, 'rgba(9,9,11,0.75)');
    ctx.fillStyle = sideScrim;
    ctx.fillRect(panelX - 300, 0, width - (panelX - 300), imageHeight);
  }
  drawAccentBar(ctx, options.accentColor, 0, 0, width, 24);

  drawQrPanel(ctx, options.qrImage, panelX, panelY, qrSize);

  ctx.textAlign = 'left';
  ctx.textBaseline = 'alphabetic';

  const textTop = options.vehicleImage ? Math.round(height * 0.52) : 420;
  drawBrand(ctx, leftPad, textTop, 88);

  ctx.fillStyle = COLORS.text;
  ctx.font = font(800, 150);
  const nameLines = wrapText(ctx, options.vehicleName, textMax, 3);
  let y = textTop + 210;
  for (const line of nameLines) {
    ctx.fillText(line, leftPad, y);
    y += 172;
  }

  ctx.fillStyle = COLORS.muted;
  ctx.font = font(500, 66);
  ctx.fillText('Scan to see every mod', leftPad, height - 300);
  ctx.fillStyle = COLORS.text;
  ctx.font = font(600, 70);
  ctx.fillText(truncate(ctx, options.shortUrl, textMax), leftPad, height - 190);
}

/**
 * Renders a printable QR sticker to a PNG blob at 300 DPI-equivalent size.
 */
export async function renderQrSticker(options: QrStickerOptions): Promise<Blob> {
  const spec = QR_STICKER_TEMPLATES[options.template];
  const canvas = document.createElement('canvas');
  canvas.width = spec.width;
  canvas.height = spec.height;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Canvas is not supported in this browser');

  const opts: QrStickerOptions = {
    ...options,
    vehicleImage: spec.supportsVehicleImage ? options.vehicleImage : null,
  };

  switch (options.template) {
    case 'square_2in':
      drawSquare2In(ctx, opts);
      break;
    case 'square_sticker':
      drawSquareSticker(ctx, opts);
      break;
    case 'windshield':
      drawWindshield(ctx, opts);
      break;
    case 'event_card':
      drawEventCard(ctx, opts);
      break;
    case 'show_board':
      drawShowBoard(ctx, opts);
      break;
  }

  const blob = await new Promise<Blob | null>((resolve) =>
    canvas.toBlob(resolve, 'image/png')
  );
  if (!blob) throw new Error('Failed to export the sticker image');
  return blob;
}
